import type { BadgeVariant } from '../../../components/ui/Badge.js';
import type { Conversation } from '../../../generated/api/types.gen.js';

export type ConversationKind = Conversation['kind'];
export type ConversationStatus = Conversation['status'];

/** Status vocabulary, written once: what the person reads, never the stored code. */
export const STATUS_LABEL: Record<ConversationStatus, string> = {
  active: 'Active',
  idle: 'Idle',
  completed: 'Completed',
  errored: 'Needs attention',
};

export const STATUS_VARIANT: Record<ConversationStatus, BadgeVariant> = {
  active: 'accent',
  idle: 'neutral',
  completed: 'success',
  errored: 'danger',
};

export const KIND_LABEL: Record<ConversationKind, string> = {
  'user-agent': 'With an agent',
  'agent-agent': 'Between agents',
  team: 'Team',
  'assistant-ui': 'Assistant',
};

/** The kind filter's options, in the order the list shows them. */
export const KIND_OPTIONS: readonly { value: ConversationKind; label: string }[] = [
  { value: 'user-agent', label: KIND_LABEL['user-agent'] },
  { value: 'agent-agent', label: KIND_LABEL['agent-agent'] },
  { value: 'team', label: KIND_LABEL.team },
  { value: 'assistant-ui', label: KIND_LABEL['assistant-ui'] },
];

/** The subject types a conversation can be about, as the person names them. */
const SUBJECT_LABEL: Record<string, string> = {
  project: 'Project',
  project_thread: 'Project thread',
  project_change: 'Project change',
  task: 'Task',
  workflow: 'Workflow',
  artifact: 'Artifact',
  template: 'Template',
  tool: 'Tool',
  daemon: 'Daemon',
  'daemon-draft': 'Daemon draft',
  workforce_member: 'Employee',
};

export function subjectLabel(subjectType: string): string {
  return SUBJECT_LABEL[subjectType] ?? subjectType.replace(/[_-]/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** A plain-text snippet from the conversation's structured summary: its goals, else its open questions. */
export function summarySnippet(structuredSummary: Conversation['structuredSummary']): string | null {
  if (!structuredSummary) return null;
  const lines = structuredSummary.goals.length > 0 ? structuredSummary.goals : structuredSummary.openQuestions;
  const text = lines.join(' · ');
  if (!text) return null;
  return text.length > 140 ? `${text.slice(0, 140)}…` : text;
}
